/**
 * Stripe Payment Service
 * 
 * Handles Stripe Checkout integration
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getApiClient } from '../api/client';
import { API_PATHS } from '@/config/api.config';
import type {
  TransactionModel,
  TransactionResponse,
  ProviderConfigurationResponse,
} from '@/types';

/**
 * Stripe instance (lazy-loaded)
 */
let stripeInstance: Stripe | null = null;

/**
 * Initialize Stripe
 * Lazy-loads the Stripe SDK by fetching public key from provider-configuration endpoint
 */
export async function initializeStripe(companyId: number): Promise<Stripe> {
  if (stripeInstance) {
    return stripeInstance;
  }

  try {
    const client = getApiClient();
    const url = API_PATHS.providerConfig
      .replace(':companyId', companyId.toString());
    
    const response = await client.get<ProviderConfigurationResponse>(url);
    const publishableKey = response.data.item.publicKey;

    if (!publishableKey) {
      throw new Error('Stripe public key not found in provider configuration');
    }
    
    const stripe = await loadStripe(publishableKey);

    if (!stripe) {
      throw new Error('Failed to load Stripe SDK');
    }

    stripeInstance = stripe;
    return stripe;
  } catch (error) {
    console.error('[StripeService] Error initializing Stripe:', error);
    throw error;
  }
}

/**
 * Create Stripe Checkout Session
 * Calls backend /online/checkout endpoint to create session
 * Session ID is returned in the providerToken attribute
 * 
 * @param request - Checkout session request data
 * @returns Checkout session response with providerToken containing session ID
 */
export async function createCheckoutSession(
  request: TransactionModel
): Promise<TransactionResponse> {
  const client = getApiClient();

  try {
    const response = await client.post<TransactionResponse>(
      API_PATHS.createCheckoutSession,
      request
    );

    return response.data;
  } catch (error) {
    console.error('[StripeService] Error creating checkout session:', error);
    throw error;
  }
}

/**
 * Redirect to Stripe Checkout
 * Redirects user to Stripe hosted payment page
 * 
 * @param sessionId - Stripe Checkout session ID (from providerToken)
 * @param companyId - Company ID for initializing Stripe
 */
export async function redirectToCheckout(sessionId: string, companyId: number): Promise<void> {
  try {
    const stripe = await initializeStripe(companyId);

    const result = await stripe.redirectToCheckout({ sessionId });

    if (result.error) {
      console.error('[StripeService] Redirect error:', result.error);
      throw new Error(result.error.message || 'Failed to redirect to checkout');
    }
  } catch (error) {
    console.error('[StripeService] Error redirecting to checkout:', error);
    throw error;
  }
}

/**
 * Create checkout session and redirect in one step
 * Convenience method that combines createCheckoutSession and redirectToCheckout
 * Session ID is extracted from the providerToken attribute in the response
 * 
 * @param request - Checkout session request data
 */
export async function createAndRedirectToCheckout(
  request: TransactionModel
): Promise<void> {
  try {
    // Create session using /online/checkout endpoint
    const response = await createCheckoutSession(request);

    // Extract session ID from providerToken in the transaction item
    const sessionId = response.item.providerToken;
    
    if (!sessionId) {
      throw new Error('Session ID not found in providerToken');
    }

    // Redirect to Stripe using the session ID from providerToken
    await redirectToCheckout(sessionId, request.companyId);
  } catch (error) {
    console.error('[StripeService] Error in create and redirect:', error);
    throw error;
  }
}

/**
 * Verify payment after redirect back from Stripe
 * Calls backend to verify payment status
 * 
 * @param sessionId - Stripe Checkout session ID
 * @param companyId - Company ID
 * @returns Payment verification response
 */
export async function verifyPayment(sessionId: string, companyId: number): Promise<TransactionResponse> {
  const client = getApiClient();

  try {
    const url = API_PATHS.verifyPayment.replace(':companyId', companyId.toString()).replace(':sessionId', sessionId);
    const response = await client.post<TransactionResponse>(url);

    return response.data;
  } catch (error) {
    console.error('[StripeService] Error verifying payment:', error);
    throw error;
  }
}

/**
 * Check if Stripe is in test mode
 * Based on the publishable key prefix from the Stripe instance
 */
export function isStripeTestMode(): boolean {
  // Since we load the key dynamically, we can't determine test mode until initialized
  // This would need the actual key to be checked at runtime
  return false;
}

/**
 * Get Stripe instance (if initialized)
 * Returns null if not yet initialized
 */
export function getStripeInstance(): Stripe | null {
  return stripeInstance;
}
